/**
 * Cascading Year → Make → Model picker.
 * Works in the browser (custom element) and in Node tests (module.exports).
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }
  if (typeof root !== 'undefined') {
    root.VehicleConfigPicker = api;
  }
  if (typeof HTMLElement !== 'undefined' && typeof customElements !== 'undefined') {
    if (!customElements.get('vehicle-config-picker')) {
      customElements.define('vehicle-config-picker', api.VehicleConfigPickerElement);
    }
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  function coversYear(vehicle, year) {
    if (year < vehicle.yearStart) return false;
    if (vehicle.yearEnd == null) return true;
    return year <= vehicle.yearEnd;
  }

  function yearsFromVehicles(vehicles, nowYear) {
    var years = {};
    var i;
    var vehicle;
    var end;
    var y;
    for (i = 0; i < vehicles.length; i++) {
      vehicle = vehicles[i];
      end = vehicle.yearEnd == null ? nowYear : vehicle.yearEnd;
      for (y = vehicle.yearStart; y <= end; y++) {
        years[y] = true;
      }
    }
    return Object.keys(years)
      .map(Number)
      .sort(function (a, b) {
        return b - a;
      });
  }

  function vehiclesForYear(vehicles, year) {
    return vehicles.filter(function (vehicle) {
      return coversYear(vehicle, year);
    });
  }

  function sortMakes(makes, makeOrder) {
    var order = makeOrder || [];
    return makes.slice().sort(function (a, b) {
      var ia = order.indexOf(a);
      var ib = order.indexOf(b);
      if (ia === -1 && ib === -1) return a.localeCompare(b);
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
  }

  function makesForYear(vehicles, year, makeOrder) {
    var seen = {};
    var makes = [];
    vehiclesForYear(vehicles, year).forEach(function (vehicle) {
      if (!seen[vehicle.make]) {
        seen[vehicle.make] = true;
        makes.push(vehicle.make);
      }
    });
    return sortMakes(makes, makeOrder);
  }

  function vehiclesForMake(vehicles, make) {
    if (!make) return vehicles;
    return vehicles.filter(function (vehicle) {
      return vehicle.make === make;
    });
  }

  function yearsFromVehiclesForMake(vehicles, make, nowYear) {
    return yearsFromVehicles(vehiclesForMake(vehicles, make), nowYear);
  }

  var PLATFORM_MAKE_PREFIXES = [
    { prefix: 'platform-porsche', make: 'Porsche' },
    { prefix: 'platform-ferrari', make: 'Ferrari' },
    { prefix: 'platform-lamborghini', make: 'Lamborghini' },
    { prefix: 'platform-mclaren', make: 'McLaren' },
    { prefix: 'platform-audi', make: 'Audi' },
    { prefix: 'platform-991', make: 'Porsche' },
    { prefix: 'platform-992', make: 'Porsche' },
    { prefix: 'platform-911', make: 'Porsche' }
  ];

  function makeFromPlatformHandle(handle) {
    var normalized = String(handle || '').toLowerCase();
    var i;
    var entry;
    if (!normalized || normalized === 'platforms') return '';
    for (i = 0; i < PLATFORM_MAKE_PREFIXES.length; i++) {
      entry = PLATFORM_MAKE_PREFIXES[i];
      if (normalized === entry.prefix || normalized.indexOf(entry.prefix + '-') === 0) {
        return entry.make;
      }
    }
    return '';
  }

  function modelsForYearMake(vehicles, year, make) {
    var seen = {};
    var models = [];
    vehiclesForYear(vehicles, year).forEach(function (vehicle) {
      if (vehicle.make !== make) return;
      if (!seen[vehicle.model]) {
        seen[vehicle.model] = true;
        models.push(vehicle.model);
      }
    });
    return models.sort(function (a, b) {
      return a.localeCompare(b);
    });
  }

  function findVehicle(vehicles, year, make, model) {
    var i;
    var vehicle;
    for (i = 0; i < vehicles.length; i++) {
      vehicle = vehicles[i];
      if (vehicle.make === make && vehicle.model === model && coversYear(vehicle, year)) {
        return vehicle;
      }
    }
    return null;
  }

  function escapeOption(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function fillSelect(select, values, placeholder) {
    var html = '<option value="">' + escapeOption(placeholder) + '</option>';
    values.forEach(function (value) {
      var safe = escapeOption(value);
      html += '<option value="' + safe + '">' + safe + '</option>';
    });
    select.innerHTML = html;
    select.value = '';
  }

  function vehicleKey(vehicle) {
    return [
      vehicle.make,
      vehicle.model,
      vehicle.yearStart,
      vehicle.yearEnd,
      vehicle.url
    ].join('\t');
  }

  function mergeCatalogPages(chunks) {
    var seen = {};
    var vehicles = [];
    var i;
    var j;
    var list;
    var vehicle;
    var key;
    for (i = 0; i < chunks.length; i++) {
      list = (chunks[i] && chunks[i].vehicles) || [];
      for (j = 0; j < list.length; j++) {
        vehicle = list[j];
        key = vehicleKey(vehicle);
        if (seen[key]) continue;
        seen[key] = true;
        vehicles.push(vehicle);
      }
    }
    return vehicles;
  }

  function catalogPageUrl(baseUrl, page) {
    var url;
    try {
      url = new URL(baseUrl, 'https://example.invalid');
    } catch (err) {
      return baseUrl;
    }
    if (page > 1) {
      url.searchParams.set('page', String(page));
    } else {
      url.searchParams.delete('page');
    }
    if (/^https?:\/\//i.test(baseUrl)) {
      return url.toString();
    }
    return url.pathname + url.search;
  }

  function parseCatalogJson(text) {
    var raw = String(text || '');
    var startMark = '<!--vehicle-config-catalog-->';
    var endMark = '<!--/vehicle-config-catalog-->';
    var start = raw.indexOf(startMark);
    var end = raw.indexOf(endMark);
    if (start !== -1 && end > start) {
      raw = raw.slice(start + startMark.length, end);
    } else {
      start = raw.indexOf('{');
      end = raw.lastIndexOf('}');
      if (start === -1 || end <= start) {
        throw new Error('catalog json missing');
      }
      raw = raw.slice(start, end + 1);
    }
    return JSON.parse(raw);
  }

  function parseCatalogResponse(response) {
    if (response && typeof response.text === 'function') {
      if (response.ok === false) {
        return Promise.reject(new Error('catalog ' + response.status));
      }
      return response.text().then(parseCatalogJson);
    }
    if (response && typeof response.json === 'function') {
      if (response.ok === false) {
        return Promise.reject(new Error('catalog ' + response.status));
      }
      return response.json();
    }
    return Promise.resolve(response);
  }

  function loadAllCatalogPages(baseUrl, fetchFn) {
    var get = fetchFn || fetch;

    function fetchPage(page) {
      return get(catalogPageUrl(baseUrl, page)).then(parseCatalogResponse);
    }

    function fetchPageRetry(page) {
      return fetchPage(page).catch(function () {
        return fetchPage(page);
      });
    }

    return fetchPageRetry(1).then(function (first) {
      var pages = Number(first && first.pages) || 1;
      var expected = Number(first && first.items);
      var rest;
      var p;
      if (pages < 1) pages = 1;
      if (pages > 50) pages = 50;
      if (pages <= 1) {
        return {
          vehicles: mergeCatalogPages([first]),
          items: first && first.items,
          pages: pages,
          incomplete: false
        };
      }
      rest = [];
      for (p = 2; p <= pages; p++) {
        rest.push(
          fetchPageRetry(p).then(
            function (chunk) {
              return chunk;
            },
            function () {
              return { vehicles: [] };
            }
          )
        );
      }
      return Promise.all(rest).then(function (more) {
        var vehicles = mergeCatalogPages([first].concat(more));
        return {
          vehicles: vehicles,
          items: first && first.items,
          pages: pages,
          incomplete: Number.isFinite(expected) && expected > vehicles.length
        };
      });
    });
  }

  var VehicleConfigPickerElement = null;
  if (typeof HTMLElement !== 'undefined') {
    VehicleConfigPickerElement = class extends HTMLElement {
      connectedCallback() {
        if (this._bound) return;
        this._bound = true;
        this._year = this.querySelector('[data-vehicle-year]');
        this._make = this.querySelector('[data-vehicle-make]');
        this._model = this.querySelector('[data-vehicle-model]');
        this._submit = this.querySelector('[data-vehicle-submit]');
        this._status = this.querySelector('[data-vehicle-status]');
        var catalogUrl = this.getAttribute('data-catalog-url');
        var feedUrl = this.getAttribute('data-catalog-feed');
        var inline = this.querySelector('script[data-vehicle-catalog]');
        var self = this;
        var fromInline = null;
        if (inline && inline.textContent) {
          try {
            fromInline = JSON.parse(inline.textContent);
          } catch (err) {
            fromInline = null;
          }
        }

        function applyCatalog(vehicles, makeOrder, statusMessage) {
          var catalog = {
            makeOrder: makeOrder || (fromInline && fromInline.makeOrder) || [],
            vehicles: vehicles || []
          };
          self._catalog = catalog;
          self._init(catalog);
          if (statusMessage) self._setStatus(statusMessage);
        }

        function fromInlineOrAsset() {
          if (fromInline && fromInline.vehicles && fromInline.vehicles.length) {
            applyCatalog(fromInline.vehicles, fromInline.makeOrder);
            return;
          }
          if (!catalogUrl) {
            self._setStatus('Vehicle list is missing.');
            return;
          }
          fetch(catalogUrl)
            .then(function (response) {
              if (!response.ok) throw new Error('catalog ' + response.status);
              return response.json();
            })
            .then(function (catalog) {
              applyCatalog(
                catalog.vehicles || [],
                (fromInline && fromInline.makeOrder) || catalog.makeOrder
              );
            })
            .catch(function () {
              self._setStatus('Could not load the vehicle list. Refresh and try again.');
            });
        }

        if (feedUrl) {
          loadAllCatalogPages(feedUrl)
            .then(function (result) {
              if (!result.vehicles || !result.vehicles.length) {
                throw new Error('empty feed');
              }
              applyCatalog(
                result.vehicles,
                fromInline && fromInline.makeOrder,
                result.incomplete
                  ? 'Could not load the full vehicle list. Refresh and try again.'
                  : ''
              );
            })
            .catch(function () {
              fromInlineOrAsset();
            });
          return;
        }

        fromInlineOrAsset();
      }

      _setStatus(message) {
        if (this._status) this._status.textContent = message || '';
      }

      _init(catalog) {
        var vehicles = catalog.vehicles || [];
        var nowYear = new Date().getFullYear();
        var years;
        var makeField;
        this._lockedMake = this.getAttribute('data-locked-make') || '';
        if (this._lockedMake) {
          years = yearsFromVehiclesForMake(vehicles, this._lockedMake, nowYear);
          fillSelect(this._year, years, this._year.getAttribute('data-placeholder') || 'Year');
          this._year.disabled = years.length === 0;
          fillSelect(this._make, [this._lockedMake], this._make.getAttribute('data-placeholder') || 'Make');
          this._make.value = this._lockedMake;
          this._make.disabled = true;
          makeField = this._make.closest('.vehicle-config-picker__field');
          if (makeField) makeField.classList.add('vehicle-config-picker__field--locked');
          this._resetModel();
        } else {
          years = yearsFromVehicles(vehicles, nowYear);
          fillSelect(this._year, years, this._year.getAttribute('data-placeholder') || 'Year');
          this._year.disabled = false;
          this._resetMake();
          this._resetModel();
        }
        this._updateSubmit();
        var self = this;
        this._year.addEventListener('change', function () {
          self._onYearChange();
        });
        this._make.addEventListener('change', function () {
          self._onMakeChange();
        });
        this._model.addEventListener('change', function () {
          self._updateSubmit();
        });
        this.querySelector('form').addEventListener('submit', function (event) {
          event.preventDefault();
          self._go();
        });
      }

      _resetMake() {
        fillSelect(this._make, [], this._make.getAttribute('data-placeholder') || 'Make');
        this._make.disabled = true;
      }

      _resetModel() {
        fillSelect(this._model, [], this._model.getAttribute('data-placeholder') || 'Model');
        this._model.disabled = true;
      }

      _onYearChange() {
        var year = Number(this._year.value);
        var models;
        this._resetModel();
        if (!year) {
          if (!this._lockedMake) {
            this._resetMake();
          }
          this._updateSubmit();
          return;
        }
        if (this._lockedMake) {
          models = modelsForYearMake(this._catalog.vehicles, year, this._lockedMake);
          fillSelect(this._model, models, this._model.getAttribute('data-placeholder') || 'Model');
          this._model.disabled = models.length === 0;
          this._updateSubmit();
          return;
        }
        var makes = makesForYear(this._catalog.vehicles, year, this._catalog.makeOrder);
        fillSelect(this._make, makes, this._make.getAttribute('data-placeholder') || 'Make');
        this._make.disabled = makes.length === 0;
        this._updateSubmit();
      }

      _onMakeChange() {
        var year = Number(this._year.value);
        var make = this._make.value;
        if (!year || !make) {
          this._resetModel();
          this._updateSubmit();
          return;
        }
        var models = modelsForYearMake(this._catalog.vehicles, year, make);
        fillSelect(this._model, models, this._model.getAttribute('data-placeholder') || 'Model');
        this._model.disabled = models.length === 0;
        this._updateSubmit();
      }

      _selectedVehicle() {
        var year = Number(this._year.value);
        var make = this._make.value;
        var model = this._model.value;
        if (!year || !make || !model) return null;
        return findVehicle(this._catalog.vehicles, year, make, model);
      }

      _updateSubmit() {
        var vehicle = this._selectedVehicle();
        this._submit.disabled = !vehicle;
        this._setStatus('');
      }

      _go() {
        var vehicle = this._selectedVehicle();
        if (!vehicle || !vehicle.url) return;
        window.location.assign(vehicle.url);
      }
    };
  }

  return {
    coversYear: coversYear,
    yearsFromVehicles: yearsFromVehicles,
    vehiclesForMake: vehiclesForMake,
    yearsFromVehiclesForMake: yearsFromVehiclesForMake,
    makeFromPlatformHandle: makeFromPlatformHandle,
    PLATFORM_MAKE_PREFIXES: PLATFORM_MAKE_PREFIXES,
    makesForYear: makesForYear,
    modelsForYearMake: modelsForYearMake,
    findVehicle: findVehicle,
    vehicleKey: vehicleKey,
    mergeCatalogPages: mergeCatalogPages,
    catalogPageUrl: catalogPageUrl,
    loadAllCatalogPages: loadAllCatalogPages,
    parseCatalogJson: parseCatalogJson,
    VehicleConfigPickerElement: VehicleConfigPickerElement
  };
});
